import { Injectable } from '@angular/core';

export type Language = 'de' | 'en';

type Dictionary = Record<string, string>;

const TRANSLATIONS: Record<Language, Dictionary> = {
  de: {
    'app.name': 'Flexara',
    'app.subtitle': 'Waehlen Sie einen Workflow und arbeiten Sie pro Anwendungsfall getrennt.',
    'nav.configure': 'Konfiguration',
    'nav.userConfigure': 'Produkt konfigurieren',
    'nav.adminProducts': 'Admin Produkte',
    'nav.adminRules': 'Admin Regeln',
    'nav.adminPricing': 'Admin Preise',
    'theme.light': 'Helles Design',
    'theme.dark': 'Dunkles Design',
    'auth.login': 'Anmelden',
    'auth.logout': 'Abmelden',
    'auth.callbackFailed': 'Authentifizierungs-Callback fehlgeschlagen.',
    'auth.tenant': 'Mandant: {tenant}',
    'auth.notAuthenticated': 'Nicht authentifiziert',
    'lang.de': 'Deutsch',
    'lang.en': 'Englisch',
    'common.refresh': 'Neu laden',
    'common.edit': 'Bearbeiten',
    'common.delete': 'Loeschen',
    'common.clear': 'Leeren',
    'common.expand': 'Ausklappen',
    'common.collapse': 'Einklappen',
    'common.filter': 'Filter',
    'common.all': 'Alle',
    'common.pageSize': 'Seitengroesse',
    'common.page': 'Seite {page} / {total} ({items} Eintraege)',
    'common.previous': 'Zurueck',
    'common.next': 'Weiter',
    'common.none': 'Keine',
    'common.yes': 'Ja',
    'common.no': 'Nein',
    'common.audit.ct': 'Erstellt am',
    'common.audit.mt': 'Geaendert am',
    'common.audit.cu': 'Erstellt von',
    'common.audit.mu': 'Geaendert von',
    'common.cancel': 'Abbrechen',
    'common.confirmDelete': 'Loeschen bestaetigen',
    'common.loadingDependent': 'Abhaengige Eintraege werden geladen...',
    'common.noDependents': 'Keine abhaengigen Eintraege gefunden.',
    'common.dependentsWillDelete': 'Folgende abhaengige Eintraege werden geloescht:',
    'common.actionCannotUndo': 'Diese Aktion kann nicht rueckgaengig gemacht werden.',
    'common.requestFailedJwt': 'Anfrage fehlgeschlagen. Backend-Verfuegbarkeit und JWT-Token pruefen.',
    'common.requestFailedApiJwt': 'Anfrage fehlgeschlagen. API-Verfuegbarkeit und JWT-Token pruefen.',

    'config.title': 'Benutzer: Konfigurationssitzung',
    'config.productId': 'Produkt-ID',
    'config.productVersionId': 'Produktversions-ID',
    'config.configurationId': 'Konfigurations-ID',
    'config.createSession': 'Sitzung erstellen',
    'config.loadSession': 'Sitzung laden',
    'config.addOptions': 'Optionscodes hinzufuegen (CSV)',
    'config.removeOptions': 'Optionscodes entfernen (CSV)',
    'config.patchSelections': 'Auswahlen patchen',
    'config.validate': 'Validieren',
    'config.price': 'Preis berechnen',
    'config.submit': 'Absenden',
    'config.configuration': 'Konfiguration',
    'config.validation': 'Validierung',
    'config.pricing': 'Preis',
    'config.error.configurationIdRequired': 'Konfigurations-ID ist erforderlich.',
    'config.error.loadFirst': 'Laden Sie zuerst eine Konfiguration.',
    'config.info.created': 'Konfiguration erstellt: {id}',

    'rule.title': 'Admin: Regelwerk',
    'rule.productId': 'Produkt-ID',
    'rule.rulesetName': 'Regelset-Name',
    'rule.rulesetId': 'Regelset-ID',
    'rule.ruleName': 'Regelname',
    'rule.severity': 'Schweregrad',
    'rule.leftOption': 'Linker Optionscode',
    'rule.rightOption': 'Rechter Optionscode',
    'rule.message': 'Meldung',
    'rule.severity.error': 'Fehler',
    'rule.severity.warn': 'Warnung',
    'rule.createRuleset': 'Regelset erstellen',
    'rule.publishRuleset': 'Regelset veroeffentlichen',
    'rule.addImplies': 'IMPLIES-Regel hinzufuegen',
    'rule.error.productIdRequired': 'Produkt-ID ist erforderlich.',
    'rule.error.rulesetIdRequired': 'Regelset-ID ist erforderlich.',
    'rule.info.rulesetCreated': 'Regelset erstellt: {id}',
    'rule.info.ruleCreated': 'Regel erstellt: {id}',
    'rule.info.rulesetPublished': 'Regelset veroeffentlicht.',

    'pricing.title': 'Admin: Preise',
    'pricing.pricebookName': 'Preisbuch-Name',
    'pricing.currency': 'Waehrung',
    'pricing.pricebookId': 'Preisbuch-ID',
    'pricing.ruleType': 'Regeltyp',
    'pricing.ruleType.base': 'Basispreis',
    'pricing.ruleType.delta': 'Optionsaufschlag',
    'pricing.ruleType.conditional': 'Bedingte Anpassung',
    'pricing.conditionType': 'Bedingungstyp',
    'pricing.condition.always': 'Immer',
    'pricing.condition.optionSelected': 'Option gewaehlt',
    'pricing.conditionOptionCode': 'Optionscode (OPTION_SELECTED)',
    'pricing.priority': 'Prioritaet',
    'pricing.amount': 'Betrag',
    'pricing.breakdownCode': 'Aufschluesselungscode',
    'pricing.reason': 'Grund',
    'pricing.createPricebook': 'Preisbuch erstellen',
    'pricing.publishPricebook': 'Preisbuch veroeffentlichen',
    'pricing.addPriceRule': 'Preisregel hinzufuegen',
    'pricing.error.pricebookIdRequired': 'Preisbuch-ID ist erforderlich.',
    'pricing.info.pricebookCreated': 'Preisbuch erstellt: {id}',
    'pricing.info.priceRuleCreated': 'Preisregel erstellt: {id}',
    'pricing.info.pricebookPublished': 'Preisbuch veroeffentlicht.',

    'product.title': 'Admin: Produktkonfiguration',
    'product.section.products': 'Produkte',
    'product.section.components': 'Komponenten',
    'product.section.structures': 'Strukturen',
    'product.section.compatibilities': 'Kompatibilitaeten',
    'product.categories': 'Produktkategorien',
    'product.products': 'Produkte',
    'product.componentTypes': 'Komponententypen',
    'product.components': 'Komponenten',
    'product.productStructure': 'Produktstruktur',
    'product.compatibilities': 'Kompatibilitaeten',
    'product.code': 'Code',
    'product.name': 'Name',
    'product.description': 'Beschreibung',
    'product.category': 'Kategorie',
    'product.type': 'Typ',
    'product.product': 'Produkt',
    'product.component': 'Komponente',
    'product.componentType': 'Komponententyp',
    'product.quantity': 'Menge',
    'product.order': 'Reihenfolge',
    'product.required': 'Pflicht',
    'product.default': 'Standard',
    'product.actions': 'Aktionen',
    'product.selectType': 'Typ auswaehlen',
    'product.selectProduct': 'Produkt auswaehlen',
    'product.selectComponent': 'Komponente auswaehlen',
    'product.searchCategories': 'Kategorien suchen',
    'product.searchProducts': 'Produkte suchen',
    'product.searchComponentTypes': 'Komponententypen suchen',
    'product.searchComponents': 'Komponenten suchen',
    'product.searchStructures': 'Strukturen suchen',
    'product.searchCompatibilities': 'Kompatibilitaeten suchen',
    'product.filter.category': 'Kategorie',
    'product.filter.type': 'Typ',
    'product.filter.product': 'Produkt',
    'product.createCategory': 'Kategorie erstellen',
    'product.updateCategory': 'Kategorie aktualisieren',
    'product.createProduct': 'Produkt erstellen',
    'product.updateProduct': 'Produkt aktualisieren',
    'product.createType': 'Typ erstellen',
    'product.updateType': 'Typ aktualisieren',
    'product.createComponent': 'Komponente erstellen',
    'product.updateComponent': 'Komponente aktualisieren',
    'product.createStructure': 'Struktur erstellen',
    'product.updateStructure': 'Struktur aktualisieren',
    'product.createCompatibility': 'Kompatibilitaet erstellen',
    'product.updateCompatibility': 'Kompatibilitaet aktualisieren',
    'product.noCategories': 'Keine Kategorien konfiguriert.',
    'product.noProducts': 'Keine Produkte konfiguriert.',
    'product.noComponentTypes': 'Keine Komponententypen konfiguriert.',
    'product.noComponents': 'Keine Komponenten konfiguriert.',
    'product.noStructures': 'Keine Produktstrukturen konfiguriert.',
    'product.noCompatibilities': 'Keine Kompatibilitaeten konfiguriert.',
    'product.refresh.categories': 'Kategorien neu laden',
    'product.refresh.products': 'Produkte neu laden',
    'product.refresh.componentTypes': 'Komponententypen neu laden',
    'product.refresh.components': 'Komponenten neu laden',
    'product.refresh.structures': 'Strukturen neu laden',
    'product.refresh.compatibilities': 'Kompatibilitaeten neu laden',
    'product.delete.dependentsCount': 'Es werden auch {count} abhaengige Eintraege geloescht.',
    'product.delete.noDependents': 'Es werden keine abhaengigen Eintraege geloescht.',
    'product.delete.categoryTitle': 'Kategorie "{name}" loeschen?',
    'product.delete.productTitle': 'Produkt "{name}" loeschen?',
    'product.delete.componentTypeTitle': 'Komponententyp "{name}" loeschen?',
    'product.delete.componentTitle': 'Komponente "{name}" loeschen?',
    'product.delete.structureTitle': 'Diese Produktstruktur loeschen?',
    'product.delete.compatibilityTitle': 'Diese Kompatibilitaet loeschen?',
    'product.error.categoryRequired': 'Kategorie-Code und Name sind erforderlich.',
    'product.error.productRequired': 'Produkt-Code und Name sind erforderlich.',
    'product.error.componentTypeRequired': 'Komponententyp-Code und Name sind erforderlich.',
    'product.error.componentRequired': 'Komponententyp, Code und Name sind erforderlich.',
    'product.error.structureRequired': 'Produkt, Komponententyp, Menge >= 1 und Reihenfolge >= 1 sind erforderlich.',
    'product.error.compatibilityRequired': 'Produkt und Komponente sind erforderlich.',
    'product.info.categoryUpdated': 'Kategorie aktualisiert.',
    'product.info.categoryCreated': 'Kategorie erstellt.',
    'product.info.categoryDeleted': 'Kategorie geloescht.',
    'product.info.productUpdated': 'Produkt aktualisiert.',
    'product.info.productCreated': 'Produkt erstellt.',
    'product.info.productDeleted': 'Produkt geloescht.',
    'product.info.componentTypeUpdated': 'Komponententyp aktualisiert.',
    'product.info.componentTypeCreated': 'Komponententyp erstellt.',
    'product.info.componentTypeDeleted': 'Komponententyp geloescht.',
    'product.info.componentUpdated': 'Komponente aktualisiert.',
    'product.info.componentCreated': 'Komponente erstellt.',
    'product.info.componentDeleted': 'Komponente geloescht.',
    'product.info.structureUpdated': 'Produktstruktur aktualisiert.',
    'product.info.structureCreated': 'Produktstruktur erstellt.',
    'product.info.structureDeleted': 'Produktstruktur geloescht.',
    'product.info.compatibilityUpdated': 'Kompatibilitaet aktualisiert.',
    'product.info.compatibilityCreated': 'Kompatibilitaet erstellt.',
    'product.info.compatibilityDeleted': 'Kompatibilitaet geloescht.',
    'entity.category': 'Kategorie',
    'entity.product': 'Produkt',
    'entity.component-type': 'Komponententyp',
    'entity.component': 'Komponente',
    'entity.product-structure': 'Produktstruktur',
    'entity.compatibility': 'Kompatibilitaet'
    ,
    'userConfig.title': 'Benutzer: Produktkonfiguration',
    'userConfig.baseProduct': 'Basisprodukt',
    'userConfig.selectProduct': 'Produkt auswaehlen',
    'userConfig.savedConfigurations': 'Gespeicherte Konfigurationen',
    'userConfig.load': 'Konfiguration laden',
    'userConfig.required': 'Pflicht',
    'userConfig.default': 'Standard',
    'userConfig.price': 'Preis',
    'userConfig.infoIcon': 'Info',
    'userConfig.noDescription': 'Keine Beschreibung hinterlegt.',
    'userConfig.noOptions': 'Keine kompatiblen Komponenten verfuegbar.',
    'userConfig.saveTitle': 'Konfiguration speichern',
    'userConfig.currentConfiguration': 'Ihre Konfiguration',
    'userConfig.saveName': 'Name',
    'userConfig.save': 'Speichern',
    'userConfig.info.saved': 'Konfiguration gespeichert.',
    'userConfig.info.loaded': 'Konfiguration geladen.',
    'userConfig.error.productRequired': 'Bitte zuerst ein Basisprodukt auswaehlen.',
    'userConfig.error.tooManySelected': 'Maximal {quantity} Auswahl(en) in diesem Schritt erlaubt.'
  },
  en: {
    'app.name': 'Flexara',
    'app.subtitle': 'Choose a workflow and iterate independently per use-case.',
    'nav.configure': 'User Configure',
    'nav.userConfigure': 'Product Configure',
    'nav.adminProducts': 'Admin Products',
    'nav.adminRules': 'Admin Rules',
    'nav.adminPricing': 'Admin Pricing',
    'theme.light': 'Light Theme',
    'theme.dark': 'Dark Theme',
    'auth.login': 'Login',
    'auth.logout': 'Logout',
    'auth.callbackFailed': 'Authentication callback failed.',
    'auth.tenant': 'Tenant: {tenant}',
    'auth.notAuthenticated': 'Not authenticated',
    'rule.severity.error': 'Error',
    'rule.severity.warn': 'Warning',
    'lang.de': 'German',
    'lang.en': 'English',
    'common.refresh': 'Reload',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.clear': 'Clear',
    'common.expand': 'Expand',
    'common.collapse': 'Collapse',
    'common.filter': 'Filter',
    'common.all': 'All',
    'common.pageSize': 'Page Size',
    'common.page': 'Page {page} / {total} ({items} items)',
    'common.previous': 'Previous',
    'common.next': 'Next',
    'common.none': 'None',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.audit.ct': 'Created At',
    'common.audit.mt': 'Modified At',
    'common.audit.cu': 'Created By',
    'common.audit.mu': 'Modified By',
    'common.cancel': 'Cancel',
    'common.confirmDelete': 'Confirm Delete',
    'common.loadingDependent': 'Loading dependent items...',
    'common.noDependents': 'No dependent items found.',
    'common.dependentsWillDelete': 'Dependent items that will be deleted:',
    'common.actionCannotUndo': 'This action cannot be undone.',
    'common.requestFailedJwt': 'Request failed. Check backend availability and JWT token.',
    'common.requestFailedApiJwt': 'Request failed. Check API availability and JWT token.',
    'product.filter.category': 'Category',
    'product.filter.type': 'Type',
    'product.filter.product': 'Product',
    'pricing.ruleType.base': 'Base Price',
    'pricing.ruleType.delta': 'Option Delta',
    'pricing.ruleType.conditional': 'Conditional Adjustment',
    'pricing.condition.always': 'Always',
    'pricing.condition.optionSelected': 'Option Selected',
    'userConfig.title': 'User: Product Configuration',
    'userConfig.baseProduct': 'Base Product',
    'userConfig.selectProduct': 'Select Product',
    'userConfig.savedConfigurations': 'Saved Configurations',
    'userConfig.load': 'Load Configuration',
    'userConfig.required': 'Required',
    'userConfig.default': 'Default',
    'userConfig.price': 'Price',
    'userConfig.infoIcon': 'Info',
    'userConfig.noDescription': 'No description available.',
    'userConfig.noOptions': 'No compatible components available.',
    'userConfig.saveTitle': 'Save Configuration',
    'userConfig.currentConfiguration': 'Your Configuration',
    'userConfig.saveName': 'Name',
    'userConfig.save': 'Save',
    'userConfig.info.saved': 'Configuration saved.',
    'userConfig.info.loaded': 'Configuration loaded.',
    'userConfig.error.productRequired': 'Please select a base product first.',
    'userConfig.error.tooManySelected': 'A maximum of {quantity} selection(s) is allowed in this step.'
  }
};

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly languageStorageKey = 'configurator-language';
  private activeLanguage: Language = 'de';

  constructor() {
    if (typeof localStorage !== 'undefined') {
      const storedLanguage = localStorage.getItem(this.languageStorageKey);
      if (storedLanguage === 'de' || storedLanguage === 'en') {
        this.activeLanguage = storedLanguage;
      }
    }
  }

  get language(): Language {
    return this.activeLanguage;
  }

  setLanguage(language: Language): void {
    this.activeLanguage = language;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.languageStorageKey, language);
    }
  }

  t(key: string, params?: Record<string, string | number>): string {
    const fallbackLanguage: Language = this.activeLanguage === 'de' ? 'en' : 'de';
    const template = TRANSLATIONS[this.activeLanguage][key]
      ?? TRANSLATIONS[fallbackLanguage][key]
      ?? key;

    if (!params) {
      return template;
    }

    return template.replace(/\{(\w+)\}/g, (_, name: string) => {
      const value = params[name];
      return value === undefined ? `{${name}}` : String(value);
    });
  }
}
